import { useState, useRef, useEffect, useCallback } from 'react';

export function ZoomPanImage({
  src,
  alt = 'Preview Image',
  zoom = 1,
  onZoomChange,
  onDownload,
}) {
  const [scale, setScale] = useState(zoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const initialPinchDistRef = useRef(null);
  const initialScaleRef = useRef(1);
  const lastTapRef = useRef(0);

  // Sync external zoom state
  useEffect(() => {
    setScale(zoom);
    if (zoom === 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [zoom]);

  const [isLoading, setIsLoading] = useState(Boolean(src));
  const [loadingError, setLoadingError] = useState(false);

  useEffect(() => {
    setIsLoading(Boolean(src));
    setLoadingError(false);
  }, [src]);

  const resolvedSrc = src || '';

  // Clamp position to boundaries
  const clampPosition = useCallback((newX, newY, currentScale) => {
    if (!containerRef.current || currentScale <= 1) return { x: 0, y: 0 };
    const { clientWidth, clientHeight } = containerRef.current;

    const maxOverflowX = (clientWidth * (currentScale - 1)) / 2;
    const maxOverflowY = (clientHeight * (currentScale - 1)) / 2;

    const clampedX = Math.min(Math.max(newX, -maxOverflowX), maxOverflowX);
    const clampedY = Math.min(Math.max(newY, -maxOverflowY), maxOverflowY);

    return { x: clampedX, y: clampedY };
  }, []);

  const handleZoomUpdate = useCallback(
    (newScale) => {
      const clampedScale = Math.min(Math.max(newScale, 1), 5);
      setScale(clampedScale);
      onZoomChange?.(clampedScale);
      if (clampedScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
    },
    [onZoomChange],
  );

  // Wheel zoom handler
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY < 0 ? 0.25 : -0.25;
    const newScale = scale + delta;
    handleZoomUpdate(newScale);
  };

  // Double Click / Tap handler
  const handleDoubleTap = (e) => {
    e.preventDefault();
    if (scale > 1) {
      handleZoomUpdate(1);
    } else {
      handleZoomUpdate(2);
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch gesture start
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      initialPinchDistRef.current = dist;
      initialScaleRef.current = scale;
    } else if (e.touches.length === 1) {
      // Double tap check
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        handleDoubleTap(e);
      }
      lastTapRef.current = now;

      if (scale > 1) {
        setIsDragging(true);
        startPosRef.current = {
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        };
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && initialPinchDistRef.current) {
      e.preventDefault();
      const currentDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      const scaleFactor = currentDist / initialPinchDistRef.current;
      handleZoomUpdate(initialScaleRef.current * scaleFactor);
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      const newX = e.touches[0].clientX - startPosRef.current.x;
      const newY = e.touches[0].clientY - startPosRef.current.y;
      setPosition(clampPosition(newX, newY, scale));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    initialPinchDistRef.current = null;
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (scale <= 1 || e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    startPosRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    e.preventDefault();
    const newX = e.clientX - startPosRef.current.x;
    const newY = e.clientY - startPosRef.current.y;
    setPosition(clampPosition(newX, newY, scale));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleTap}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`w-full h-full flex items-center justify-center overflow-hidden select-none touch-none ${
        scale > 1
          ? isDragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
          : 'cursor-default'
      }`}
    >
      {resolvedSrc && !loadingError && (
        <img
          src={resolvedSrc}
          alt={alt}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            console.error('[MediaViewer] image failed to load', resolvedSrc);
            setIsLoading(false);
            setLoadingError(true);
          }}
          style={{
            transform: `translate3d(${position.x}px, ${position.y}px, 0px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            backgroundColor: '#ffffff',
            display: isLoading ? 'none' : 'block',
          }}
          className="max-h-full max-w-full object-contain pointer-events-none bg-white rounded-lg"
        />
      )}
      {isLoading && !loadingError && (
        <div className="flex flex-col items-center justify-center gap-3 text-white">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-medium text-gray-300">Loading image...</p>
        </div>
      )}
      {loadingError && (
        <div className="p-8 bg-[#202c33] rounded-2xl flex flex-col items-center gap-4 text-white">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-sm font-medium text-center">
            Unable to load image
          </span>
          {onDownload && (
            <button
              onClick={onDownload}
              className="px-4 py-2 mt-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs font-semibold"
            >
              Download File
            </button>
          )}
        </div>
      )}
    </div>
  );
}
